// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// layout.ts — pure geometry. No DOM. Where a hex is, and which hex a finger meant.
//
// THE HARD PROBLEM THIS FILE EXISTS FOR, AND THE MEASUREMENT THAT SHAPED IT.
//
// brood has no board, so there is no fixed frame: the cluster starts as one tile and can end up
// sixteen columns wide, in any direction. Two requirements collide, and a sweep over 16,845 real
// frames from the balance corpus showed they cannot both hold at once:
//
//   * A tappable hex needs its neighbour 44px away. The pitch is sqrt(3)*R, so that means R >= 26.
//   * The whole cluster plus its frontier ring wants to be on screen. At 351px of usable width the
//     cluster is 8 columns wide at the median and 11 at the 90th percentile, and fitting cluster +
//     frontier at R >= 26 FAILS IN 70-73% OF PLIES.
//
// So "shrink to fit and keep 44px targets" is arithmetically impossible on a tessellation, and a
// spec that asserts both is a spec that cannot ship. The resolution is to stop treating them as
// one problem:
//
//   THE BOARD FRAME is what you READ. It fits the whole cluster and its frontier, down to R = 14
//   (a 28px tile). That clamps only above about sixteen columns, i.e. under 1% of plies — so the
//   cluster and both Matriarchs are essentially always on screen, there is no panning, and nobody
//   ever loses their Matriarch off the edge.
//
//   THE ACTION FRAME is what you ACT IN. The moment a piece is selected it refits to just that
//   piece and its candidates, with a floor of R = 26. Measured: unclamped in 87% of selections,
//   median R = 54.
//
//   AND THE 44px RULE BECOMES A RUNTIME PRECONDITION FOR COMMITTING, not a property of every
//   pixel. needsConfirm() measures the actual minimum separation between the candidates on screen;
//   under 44px, a tap AIMS and a full-width button commits. Measured cost: about 13% of selections
//   (a Strider that can reach the far side of the cluster). This is bracken's aim-then-confirm
//   step, generalised from a hard-coded special case into a measured geometric condition.
//
// HIT RESOLUTION IS HEX-FIRST, AND THAT IS A CORRECTNESS FIX RATHER THAN A REFINEMENT. An earlier
// cut resolved a tap to the nearest CANDIDATE within 1.6-2.0 x R. Both caps are wider than the
// pitch (1.732 R) and non-candidates — pinned tiles, held tiles, the enemy's tiles — are not in the
// candidate set. So a tap on the exact centre of a pinned tile resolved to a legal destination
// beside it and COMMITTED a move of a different piece. Instead: invert the pixel mapping to get the
// hex under the finger, and only then decide. The snap radius is 0.85R, strictly inside the
// inscribed circle (sqrt(3)/2 = 0.866R), so a snap can never cross into a neighbouring hex.

import { SQRT3, centre, key, keyQ, keyR, unitX, unitY } from './hex';

/** The overview floor: a 28px tile. Readable, deliberately not tappable. */
export const R_READ_MIN = 14;
/** The commit floor. sqrt(3) * 26 = 45.03px between neighbouring centres, and sqrt(3) * 25 = 43.3
 *  does not clear 44 — which is the entire derivation, and why layout.test.ts pins both halves. */
export const R_ACT_MIN = 26;
export const R_MAX = 54;
/** R is quantised, so the frame steps rather than breathing. */
export const R_STEP = 2;
/** Breathing room inside the stage, each side. */
export const PAD = 8;
/**
 * How far outside a hex's own centre a tap still snaps to it, as a multiple of R. It MUST stay
 * under sqrt(3)/2 = 0.866 — the inradius — or a snap can reach into the neighbouring hex and the
 * hex-first resolution stops meaning anything.
 */
export const SNAP = 0.85;
/** The touch-target floor every commit-without-confirm is measured against. */
export const TARGET_MIN = 44;

export interface Frame {
  /** Tile circumradius in CSS px. */
  readonly R: number;
  /** Add these to a hex centre to get stage coordinates. */
  readonly originX: number;
  readonly originY: number;
  /** True when the focus set could not be fitted at this frame's floor. */
  readonly clamped: boolean;
  readonly availW: number;
  readonly availH: number;
}

const clamp = (lo: number, v: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const floorTo = (v: number, s: number): number => Math.floor(v / s) * s;

/**
 * Fit `focus` into an available box, no smaller than `rMin`.
 *
 * Pass R_READ_MIN for the board frame (fit everything, stay readable) and R_ACT_MIN for the action
 * frame (fit the decision, stay tappable).
 */
export function computeFrame(
  focus: readonly number[],
  availW: number,
  availH: number,
  rMin: number = R_READ_MIN,
): Frame {
  const w = Math.max(1, availW);
  const h = Math.max(1, availH);
  if (focus.length === 0) {
    return { R: R_MAX, originX: w / 2, originY: h / 2, clamped: false, availW: w, availH: h };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const k of focus) {
    const ux = unitX(k);
    const uy = unitY(k);
    if (ux < minX) minX = ux;
    if (ux > maxX) maxX = ux;
    if (uy < minY) minY = uy;
    if (uy > maxY) maxY = uy;
  }

  // Content extent at R = 1: the span between centres, plus one whole tile (2 wide, sqrt3 tall).
  const spanX = maxX - minX + 2;
  const spanY = maxY - minY + SQRT3;
  const fit = Math.min((w - 2 * PAD) / spanX, (h - 2 * PAD) / spanY);
  const R = clamp(rMin, floorTo(fit, R_STEP), R_MAX);
  const contentW = R * spanX;
  const contentH = R * spanY;
  return {
    R,
    originX: (w - contentW) / 2 - R * (minX - 1),
    originY: (h - contentH) / 2 - R * (minY - SQRT3 / 2),
    clamped: fit < rMin,
    availW: w,
    availH: h,
  };
}

/** Stage-space centre of a hex under a frame. */
export function screenOf(f: Frame, k: number): { x: number; y: number } {
  const c = centre(k, f.R);
  return { x: c.x + f.originX, y: c.y + f.originY };
}

/** Is a hex's whole tile inside the visible stage? */
export function onFrame(f: Frame, k: number): boolean {
  const { x, y } = screenOf(f, k);
  const halfH = (SQRT3 * f.R) / 2;
  return x - f.R >= -1 && x + f.R <= f.availW + 1 && y - halfH >= -1 && y + halfH <= f.availH + 1;
}

/**
 * The exact inverse of `centre` — which hex contains this stage-space point. Rounding on a hex
 * grid is done in cube coordinates: round all three, then correct whichever moved furthest, which
 * is the standard construction and is exact (never off by a hex) rather than approximate.
 */
export function hexAt(f: Frame, px: number, py: number): number {
  const x = (px - f.originX) / f.R;
  const y = (py - f.originY) / f.R;
  const q = x / 1.5;
  const r = y / SQRT3 - q / 2;
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);
  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return key(rq, rr);
}

/** Distance in px from a point to a hex's centre. */
export function distanceTo(f: Frame, k: number, px: number, py: number): number {
  const { x, y } = screenOf(f, k);
  return Math.hypot(px - x, py - y);
}

/**
 * The nearest candidate within the snap radius, or null. Used ONLY after `hexAt` has established
 * that the finger is not on a real cell that means something else — it is a forgiveness radius for
 * a near miss, never a way to reach across the board.
 *
 * Duplicates are explicitly fine here. A hex genuinely can carry two different actions (a Climber's
 * destination that is also one of your other movable tiles; a Tender's own step destination, which
 * is by construction always also one of her shunt landings — measured at 76.5% of her selections),
 * so a hit test that threw on a repeated hex would throw during ordinary play. Deciding BETWEEN
 * those actions is the caller's job, not the geometry's.
 */
export function snapTo(f: Frame, cands: readonly number[], px: number, py: number): number | null {
  const cap = SNAP * f.R;
  let best: number | null = null;
  let bestD = Infinity;
  for (const k of cands) {
    const d = distanceTo(f, k, px, py);
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best !== null && bestD <= cap ? best : null;
}

/** Minimum distance between any two distinct candidate centres, in px. Infinity for 0 or 1. */
export function minSeparation(f: Frame, cands: readonly number[]): number {
  const uniq = [...new Set(cands)];
  let best = Infinity;
  for (let i = 0; i < uniq.length; i++) {
    const a = screenOf(f, uniq[i]);
    for (let j = i + 1; j < uniq.length; j++) {
      const b = screenOf(f, uniq[j]);
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (d < best) best = d;
    }
  }
  return best;
}

/**
 * Does this candidate set have to be aimed and confirmed rather than committed on a tap?
 *
 * Measured from the ACTUAL pixel positions rather than inferred from `clamped`, because those are
 * different questions: a frame can be clamped and still have well-separated candidates, and an
 * unclamped frame with a Strider's forty-four destinations can be tighter than a thumb.
 */
export const needsConfirm = (f: Frame, cands: readonly number[]): boolean =>
  minSeparation(f, cands) < TARGET_MIN;

/** The centre of a set of hexes, as a hex key. */
export function centroidKey(ks: readonly number[]): number | null {
  if (ks.length === 0) return null;
  let q = 0;
  let r = 0;
  for (const k of ks) {
    q += keyQ(k);
    r += keyR(k);
  }
  return key(Math.round(q / ks.length), Math.round(r / ks.length));
}

/** The six corners of a flat-top hex, relative to its centre. The renderer draws from this so the
 *  drawn shape and the hit test can never drift apart. */
export function corners(R: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i);
    out.push([R * Math.cos(a), R * Math.sin(a)]);
  }
  return out;
}
